import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import {
  CUSTOMER_OWNERSHIP_RULE,
  type CrossCustomerMembership,
} from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"

export type CustomerOwnershipBadgeProps = Omit<
  BadgeProps,
  "variant" | "iconSrc" | "tooltip" | "children"
> & {
  /** The owner and the team customer that disagree (see the utils). */
  crossing: CrossCustomerMembership
  /** Which side the row is read from, for the tooltip's wording. */
  perspective: "user" | "team"
}

/**
 * The amber pill a table row gets when a membership crosses customers:
 * the user is owned by one customer, the team belongs to another. Read
 * from a team's member list ("this member belongs elsewhere") or from a
 * user's team list ("this team belongs elsewhere"). Never hidden behind a
 * hover: this is the signal that was missing when a team sat under the
 * wrong customer for 22 months.
 */
function CustomerOwnershipBadge(props: CustomerOwnershipBadgeProps) {
  const { crossing, perspective, ...badgeProps } = props
  const { ownerName, teamCustomerName } = crossing

  const summary =
    perspective === "team"
      ? `Owned by ${ownerName}, while this team belongs to ${teamCustomerName}.`
      : `This team belongs to ${teamCustomerName}, while the user is owned by ${ownerName}.`

  return (
    <Badge
      variant="warning"
      size="tiny"
      iconSrc="triangle-warning"
      tooltip={`${summary}\n\n${CUSTOMER_OWNERSHIP_RULE} Put it right with Update Customer on the user, or on the team, unless the sharing is intended.`}
      {...badgeProps}
    >
      Other customer
    </Badge>
  )
}

export default CustomerOwnershipBadge
